import { Collapse } from '@vrplatform/components';
import {
  LazyTable,
  type MRT_ColumnDef,
  useTablePagination,
} from '@vrplatform/table';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import {
  Box,
  Text,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { type ComponentProps, type ReactNode, useMemo } from 'react';

export const DrawerCollapsableTableTitle = ({
  count,
  title,
  loading,
}: {
  title: string;
  loading?: boolean;
  count: ReactNode;
}) => {
  const { colors } = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();

  return (
    <>
      <Text component="span" size="sm" fw={500}>
        {title}
        {!loading && (
          <Text
            c={colorScheme === 'dark' ? colors.gray[6] : 'gray'}
            ml="xs"
            size="xs"
            component="span"
          >
            {count}
          </Text>
        )}
      </Text>
      {loading && <LoadingIndicator size={'0.75rem'} />}
    </>
  );
};

export const DrawerCollapsableTable = <TRow extends Record<string, any>>({
  rowMenu,
  columns,
  rightSection,
  rowData,
  title,
  emptyRowsFallback: Empty,
  onRowClick,
  subRows,
  customRowCount,
  paginationSize = 10,
  loading,
  defaultOpened,
  hideHeader = true,
  hideToggle = false,
  styles,
}: {
  title: string;
  rowData: TRow[];
  customRowCount?: ReactNode;
  columns: MRT_ColumnDef<TRow>[];
  rightSection: React.ReactNode;
  emptyRowsFallback: ComponentProps<
    typeof LazyTable<TRow>
  >['table']['emptyRowsFallback'];
  subRows?: ComponentProps<typeof LazyTable<TRow>>['subRows'];
  styles?: ComponentProps<typeof LazyTable<TRow>>['styles'];
  rowMenu?: ComponentProps<typeof LazyTable<TRow>>['rowMenu'];
  onRowClick?: ComponentProps<typeof LazyTable<TRow>>['table']['onRowClick'];
  paginationSize?: number;
  loading?: boolean;
  defaultOpened?: boolean;
  hideHeader?: boolean;
  hideToggle?: boolean;
}) => {
  const pagination = useTablePagination(paginationSize);

  const withPagination = rowData.length > paginationSize;

  const data = useMemo(() => {
    if (!withPagination) return rowData;

    const index = pagination.pagination.pageIndex;
    const pageSize = pagination.pagination.pageSize;
    const start = index * pageSize;
    const end = start + pageSize;
    return rowData.slice(start, end);
  }, [rowData, pagination.pagination, withPagination]);

  return (
    <Box>
      <Collapse
        title={
          <DrawerCollapsableTableTitle
            title={title}
            loading={loading}
            count={customRowCount ?? rowData.length}
          />
        }
        rightSection={rightSection}
        minHeight={30}
        defaultOpened={defaultOpened ?? !!rowData.length}
        hideToggle={hideToggle}
      >
        <Box>
          <LazyTable
            data={{
              rows: data,
              error: null,
              loading: loading ?? false,
              rowCount: rowData.length,
            }}
            table={{
              columns,
              hideHeader,
              hideTopBar: true,
              emptyRowsFallback: Empty,
              onRowClick,
            }}
            styles={styles}
            pagination={withPagination ? pagination : undefined}
            rowMenu={rowMenu}
            subRows={subRows}
          />
        </Box>
      </Collapse>
    </Box>
  );
};
